import { useState } from "react";
import { useAllFeedback, useUpdateFeedbackStatus } from "@platform/hooks";
import { PageHeader } from "../../components/shared/PageHeader";
import { useToast } from "@nthamizh/ui";
import { Bug, MessageSquare, ChevronDown, Clock } from "lucide-react";

const STATUS_OPTIONS = ["open", "under_review", "resolved", "closed"] as const;
type FeedbackStatus = (typeof STATUS_OPTIONS)[number];

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; className: string }> = {
  open: { label: "Open", className: "bg-amber-50 text-amber-700 border-amber-100" },
  under_review: { label: "Under review", className: "bg-blue-50 text-blue-700 border-blue-100" },
  resolved: { label: "Resolved", className: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  closed: { label: "Closed", className: "bg-gray-50 text-gray-500 border-gray-200" },
};

type FeedbackItem = {
  id: string;
  type: "feedback" | "bug";
  title: string;
  description: string;
  status: FeedbackStatus;
  reviewerNote?: string | null;
  createdAt: string;
  draftId?: string | null;
};

function FeedbackCard({ item }: { item: FeedbackItem }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(item.reviewerNote ?? "");
  const updateStatus = useUpdateFeedbackStatus();
  const status = STATUS_CONFIG[item.status];

  const handleStatusChange = async (newStatus: FeedbackStatus) => {
    try {
      await updateStatus.mutateAsync({ id: item.id, status: newStatus, reviewerNote: note || undefined });
      toast({ title: "Status updated", variant: "success" });
    } catch {
      toast({ title: "Could not update status", variant: "error" });
    }
  };

  return (
    <div className="border-b border-ds-border last:border-0">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-start gap-4 px-5 py-4 hover:bg-ds-bg/60 text-left transition-colors"
      >
        <div
          className={`mt-0.5 shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${
            item.type === "bug" ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-500"
          }`}
        >
          {item.type === "bug" ? <Bug className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-semibold text-ds-text truncate">{item.title}</span>
            <span
              className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-md border ${item.type === "bug" ? "bg-red-50 text-red-600 border-red-100" : "bg-blue-50 text-blue-600 border-blue-100"}`}
            >
              {item.type}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-ds-text-muted">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(item.createdAt).toLocaleDateString()}
            </span>
            {item.draftId && <span>From a ConfigIQ task</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${status.className}`}>
            {status.label}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-ds-text-muted transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 ml-12">
          <div className="bg-ds-bg rounded-lg border border-ds-border p-4 mb-4">
            <p className="text-sm text-ds-text-secondary leading-relaxed whitespace-pre-wrap">{item.description}</p>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => void handleStatusChange(s)}
                  disabled={item.status === s || updateStatus.isPending}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-100 ${
                    item.status === s
                      ? `${STATUS_CONFIG[s].className} cursor-default`
                      : "border-ds-border text-ds-text-secondary hover:border-ds-border-strong hover:bg-ds-bg"
                  }`}
                >
                  {STATUS_CONFIG[s].label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ds-text-secondary">Reviewer note (optional)</label>
              <textarea
                className="w-full rounded-lg border border-ds-border bg-white px-3 py-2 text-sm text-ds-text-secondary focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/15 resize-none"
                rows={2}
                placeholder="Add a note for the submitter…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {note !== (item.reviewerNote ?? "") && (
              <div className="flex justify-end">
                <button
                  onClick={() => void handleStatusChange(item.status)}
                  disabled={updateStatus.isPending}
                  className="px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  Save note
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PlatformFeedbackReviewPage() {
  const { data: items, isLoading } = useAllFeedback();
  const [filter, setFilter] = useState<"all" | "open" | "bug" | "feedback">("all");

  const feedbackItems = (items as FeedbackItem[] | undefined) ?? [];
  const filtered = feedbackItems.filter((item) => {
    if (filter === "open") return item.status === "open" || item.status === "under_review";
    if (filter === "bug") return item.type === "bug";
    if (filter === "feedback") return item.type === "feedback";
    return true;
  });

  const openCount = feedbackItems.filter((i) => i.status === "open" || i.status === "under_review").length;
  const bugCount = feedbackItems.filter((i) => i.type === "bug").length;

  return (
    <div className="space-y-6">
      <PageHeader title="Feedback & bugs" subtitle="Review submissions from users and track resolution." />

      {feedbackItems.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-ds-border rounded-card shadow-card p-4">
            <p className="text-[11px] font-semibold text-ds-text-muted uppercase tracking-wide mb-1">Total</p>
            <p className="text-2xl font-semibold text-ds-text">{feedbackItems.length}</p>
          </div>
          <div className="bg-white border border-ds-border rounded-card shadow-card p-4">
            <p className="text-[11px] font-semibold text-ds-text-muted uppercase tracking-wide mb-1">Open / in review</p>
            <p className="text-2xl font-semibold text-amber-600">{openCount}</p>
          </div>
          <div className="bg-white border border-ds-border rounded-card shadow-card p-4">
            <p className="text-[11px] font-semibold text-ds-text-muted uppercase tracking-wide mb-1">Bugs</p>
            <p className="text-2xl font-semibold text-red-600">{bugCount}</p>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-ds-border">
        {[
          { id: "all" as const, label: "All" },
          { id: "open" as const, label: `Open (${openCount})` },
          { id: "bug" as const, label: "Bugs" },
          { id: "feedback" as const, label: "Feedback" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-3 text-xs font-semibold border-b-2 transition-all duration-100 ${
              filter === tab.id
                ? "border-indigo-500 text-indigo-700"
                : "border-transparent text-ds-text-secondary hover:text-ds-text"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="text-sm text-ds-text-muted">Loading feedback...</div>}

      {!isLoading && filtered.length === 0 && (
        <div className="bg-white border border-ds-border rounded-card shadow-card p-8 text-center">
          <p className="text-sm text-ds-text-secondary">No items match this filter.</p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
          {filtered.map((item) => (
            <FeedbackCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
